import React, { useState, useEffect } from 'react';
import { Device, DeviceCreate, DeviceUpdate } from '../types';
import { useAuth } from '../hooks/useAuth';
import CardInput from './CardInput';
import { apiService } from '../services/api';

interface DeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  device?: Device | null;
  onSave: (deviceData: DeviceCreate | DeviceUpdate) => Promise<void>;
  mode: 'add' | 'edit';
}

const DeviceModal: React.FC<DeviceModalProps> = ({
  isOpen,
  onClose,
  device,
  onSave,
  mode
}) => {
  const { user } = useAuth();
  const isSuperUser = user?.is_superuser === 1;

  const [formData, setFormData] = useState<DeviceCreate>({
    name: '',
    model: '',
    memory: '',
    os_version: '',
    model_number: '',
    serial_number: '',
    imei: '',
    user_id_api: '',
    hash_get_pay: '',
    hash_get_new_orders: '',
    bank_name: '',
    card_number: '',
    phone: '',
    first_name: '',
    last_name: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [cardCheckResult, setCardCheckResult] = useState<{
    device_count: number;
    max_allowed: number;
    can_use: boolean;
  } | null>(null);
  const [checkingCard, setCheckingCard] = useState(false);

  const bankOptions = [
    "Т-Банк", 
    "Альфа Банк", 
    "ВТБ", 
    "Озон Банк", 
    "Газпромбанк", 
    "Сбербанк",
    "Райффайзенбанк",
    "Промсвязьбанк",
    "Россельхозбанк",
    "Другой"
  ];

  const iphoneModels = [
    "iPhone 6",
    "iPhone 6 Plus",
    "iPhone 6s",
    "iPhone 6s Plus",
    "iPhone SE (1st generation)",
    "iPhone 7",
    "iPhone 7 Plus",
    "iPhone 8",
    "iPhone 8 Plus",
    "iPhone X",
    "iPhone XR",
    "iPhone XS",
    "iPhone XS Max",
    "iPhone 11",
    "iPhone 11 Pro",
    "iPhone 11 Pro Max",
    "iPhone SE (2nd generation)",
    "iPhone 12 mini",
    "iPhone 12",
    "iPhone 12 Pro",
    "iPhone 12 Pro Max",
    "iPhone 13 mini",
    "iPhone 13",
    "iPhone 13 Pro",
    "iPhone 13 Pro Max",
    "iPhone SE (3rd generation)",
    "iPhone 14",
    "iPhone 14 Plus",
    "iPhone 14 Pro",
    "iPhone 14 Pro Max",
    "iPhone 15",
    "iPhone 15 Plus",
    "iPhone 15 Pro",
    "iPhone 15 Pro Max",
    "Другая модель"
  ];

  useEffect(() => {
    if (device && mode === 'edit') {
      setFormData({
        name: device.name || '',
        model: device.model || '',
        memory: device.memory || '',
        os_version: device.os_version || '',
        model_number: device.model_number || '',
        serial_number: device.serial_number || '',
        imei: device.imei || '',
        user_id_api: device.user_id_api || '',
        hash_get_pay: device.hash_get_pay || '',
        hash_get_new_orders: device.hash_get_new_orders || '',
        bank_name: device.bank_name || '',
        card_number: device.card_number || '',
        phone: device.phone || '',
        first_name: device.first_name || '',
        last_name: device.last_name || '',
      });
    } else {
      setFormData({
        name: '',
        model: '',
        memory: '',
        os_version: '',
        model_number: '',
        serial_number: '',
        imei: '',
        user_id_api: '',
        hash_get_pay: '',
        hash_get_new_orders: '',
        bank_name: '',
        card_number: '',
        phone: '',
        first_name: '',
        last_name: '',
      });
    }
  }, [device, mode, isOpen]);

  // Функция для проверки количества устройств с картой
  const checkCardUsage = async (cardNumber: string) => {
    if (!cardNumber || cardNumber.replace(/\s/g, '').length < 16) {
      setCardCheckResult(null);
      return;
    }

    try {
      setCheckingCard(true);
      // При редактировании передаём ID устройства, чтобы исключить его из подсчёта
      const deviceId = mode === 'edit' && device ? device.id : undefined;
      const result = await apiService.checkCardUsage(cardNumber, deviceId);
      setCardCheckResult(result);
    } catch (err) {
      console.error('Error checking card usage:', err);
      setCardCheckResult(null);
    } finally {
      setCheckingCard(false);
    }
  };

  // Проверяем карту при изменении номера
  useEffect(() => {
    if (formData.card_number && formData.card_number.trim()) {
      const timeoutId = setTimeout(() => {
        checkCardUsage(formData.card_number!);
      }, 500); // Задержка 500мс для избежания частых запросов

      return () => clearTimeout(timeoutId);
    } else {
      setCardCheckResult(null);
    }
  }, [formData.card_number]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Проверяем, можно ли использовать карту
    if (formData.card_number && cardCheckResult && !cardCheckResult.can_use) {
      setError(`Карта ${formData.card_number.replace(/\s/g, '')} уже используется на ${cardCheckResult.device_count} устройствах. Максимальное количество устройств с одной картой: ${cardCheckResult.max_allowed}`);
      setLoading(false);
      return;
    }

    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      // Проверяем, является ли ошибка связанной с превышением лимита карт
      if (err.response?.data?.detail && err.response.data.detail.includes('уже используется на')) {
        setError(err.response.data.detail);
      } else {
        setError('Ошибка сохранения устройства');
      }
      console.error('Error saving device:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}>
      {/* Контейнер: на мобильных — полноэкранный, на десктопе — как раньше */}
      <div
        className="bg-[#181A20] border border-[#23262F] shadow-xl w-full max-w-4xl sm:rounded-xl sm:max-h-[90vh] sm:flex-none sm:overflow-y-auto md:overflow-visible md:max-h-[90vh] md:w-full h-full sm:h-auto flex flex-col"
        style={{
          paddingLeft: 'calc(env(safe-area-inset-left, 0px) + var(--tg-safe-area-inset-left, 0px))',
          paddingRight: 'calc(env(safe-area-inset-right, 0px) + var(--tg-safe-area-inset-right, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--tg-safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Хедер: липкий на мобильных */}
        {/* Safe area spacer for Telegram Mini Apps */}
        <div className="md:hidden" style={{ height: 'calc(env(safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px))' }} />
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-[#23262F] sticky top-0 bg-[#181A20] z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              {mode === 'add' ? 'Добавить устройство' : 'Редактировать устройство'}
            </h2>
            <button
              onClick={onClose}
              className="text-[#B1B5C3] hover:text-white transition-colors p-2 -mr-2"
              aria-label="Закрыть"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Прокручиваемая область контента */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pt-4 pb-6 sm:py-6">
          {error && (
            <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form id="device-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Основная информация */}
            <div className="bg-[#23262F] rounded-lg p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Основная информация
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Название устройства *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Введите название устройства"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Модель *
                  </label>
                  <select
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Выберите модель</option>
                    {iphoneModels.map((model) => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Память
                  </label>
                  <input
                    type="text"
                    name="memory"
                    value={formData.memory}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Например: 128GB"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Версия ОС
                  </label>
                  <input
                    type="text"
                    name="os_version"
                    value={formData.os_version}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Например: iOS 17.0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Номер модели
                  </label>
                  <input
                    type="text"
                    name="model_number"
                    value={formData.model_number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Например: A2892"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Серийный номер
                  </label>
                  <input
                    type="text"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Серийный номер устройства"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    IMEI
                  </label>
                  <input
                    type="text"
                    name="imei"
                    value={formData.imei}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="IMEI номер"
                  />
                </div>
              </div>
            </div>

            {/* API данные */}
            <div className="bg-[#23262F] rounded-lg p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  API данные для запросов
                </h3>
                <button
                  type="button"
                  onClick={() => setShowInstructions(true)}
                  className="text-blue-400 hover:text-blue-300 transition-colors p-1"
                  title="Как получить User ID и Hash getPaySystemInfo?"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    User ID *
                  </label>
                  <input
                    type="text"
                    name="user_id_api"
                    value={formData.user_id_api}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="User ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                  Hash getPaySystemInfo *
                  </label>
                  <input
                    type="text"
                    name="hash_get_pay"
                    value={formData.hash_get_pay}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Hash getPaySystemInfo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Hash getNewOrders
                  </label>
                  <input
                    type="text"
                    name="hash_get_new_orders"
                    value={formData.hash_get_new_orders}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg text-white placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Hash getNewOrders"
                  />
                </div>
              </div>
            </div>

            {/* Реквизиты */}
            <div className={`bg-[#23262F] rounded-lg p-4 sm:p-6 ${!isSuperUser ? 'opacity-60' : ''}`}>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                {!isSuperUser ? (
                  <>
                    <svg className="w-5 h-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Реквизиты
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Реквизиты
                  </>
                )}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Банк
                  </label>
                  <select
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    disabled={!isSuperUser}
                    className={`w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isSuperUser 
                        ? 'text-white cursor-pointer' 
                        : 'text-[#6C757D] cursor-not-allowed opacity-50'
                    }`}
                  >
                    <option value="">Выберите банк</option>
                    {bankOptions.map((bank) => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Номер карты
                  </label>
                  <CardInput
                    value={formData.card_number || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, card_number: value }))}
                    disabled={!isSuperUser}
                    placeholder="xxxx yyyy xxxx yyyy"
                  />
                  {/* Индикатор проверки карты */}
                  {checkingCard && (
                    <div className="mt-2 flex items-center text-blue-400 text-sm">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Проверяем количество устройств с этой картой...
                    </div>
                  )}
                  {cardCheckResult && !checkingCard && (
                    <div className={`mt-2 flex items-center text-sm ${
                      cardCheckResult.can_use ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {cardCheckResult.can_use ? (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Карта используется на {cardCheckResult.device_count} из {cardCheckResult.max_allowed} устройств
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Карта уже используется на {cardCheckResult.device_count} устройствах. Максимум: {cardCheckResult.max_allowed}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Номер телефона
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={!isSuperUser}
                    className={`w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isSuperUser 
                        ? 'text-white cursor-text' 
                        : 'text-[#6C757D] cursor-not-allowed opacity-50'
                    }`}
                    placeholder="7XXXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Имя
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={!isSuperUser}
                    className={`w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isSuperUser 
                        ? 'text-white cursor-text' 
                        : 'text-[#6C757D] cursor-not-allowed opacity-50'
                    }`}
                    placeholder="Имя владельца"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Фамилия
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={!isSuperUser}
                    className={`w-full px-4 py-3 bg-[#181A20] border border-[#353945] rounded-lg placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isSuperUser 
                        ? 'text-white cursor-text' 
                        : 'text-[#6C757D] cursor-not-allowed opacity-50'
                    }`}
                    placeholder="Фамилия владельца"
                  />
                </div>

                {/* Поле для копирования реквизитов */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#B1B5C3] mb-2">
                    Реквизиты для копирования
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={`${(formData.card_number || '').replace(/\s/g, '')}*${formData.last_name || ''}*${formData.first_name || ''}*${formData.phone || ''}`}
                      className="w-full px-4 py-3 pr-12 bg-[#23262F] border border-[#353945] rounded-lg text-[#B1B5C3] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent select-none cursor-default"
                      placeholder="2200152929438656*Гаврилов*Егор*79090806311"
                      style={{ userSelect: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const requisites = `${(formData.card_number || '').replace(/\s/g, '')}*${formData.last_name || ''}*${formData.first_name || ''}*${formData.phone || ''}`;
                        navigator.clipboard.writeText(requisites).then(() => {
                          // Можно добавить уведомление об успешном копировании
                          console.log('Реквизиты скопированы');
                        });
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-[#B1B5C3] hover:text-white transition-colors"
                      title="Копировать реквизиты"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              {!isSuperUser && (
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 text-sm">
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Раздел находится в планах разработки
                  </p>
                </div>
              )}
            </div>

            {/* Футер действий: липкий на мобильных — отступ даем через pb-24 на контейнере выше */}
          </form>
        </div>

        <div className="px-4 sm:px-6 py-4 border-t border-[#23262F] bg-[#181A20] sticky bottom-0 z-10">
          <div className="flex gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none sm:px-6 px-4 py-3 text-sm font-medium text-[#B1B5C3] bg-[#23262F] border border-[#353945] rounded-lg hover:bg-[#2A2D36] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              form="device-form"
              disabled={loading || (cardCheckResult !== null && !cardCheckResult.can_use)}
              className={`flex-1 sm:flex-none sm:px-6 px-4 py-3 text-sm font-medium border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                loading || (cardCheckResult !== null && !cardCheckResult.can_use)
                  ? 'text-gray-400 bg-gray-600 cursor-not-allowed opacity-50'
                  : 'text-white bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Сохранение...' : (mode === 'add' ? 'Добавить устройство' : 'Сохранить изменения')}
            </button>
          </div>
        </div>
      </div>

      {/* Модальное окно с инструкцией */}
      {showInstructions && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}>
          <div
            className="bg-[#181A20] border border-[#23262F] shadow-xl w-full max-w-2xl sm:rounded-xl sm:max-h-[90vh] h-full sm:h-auto flex flex-col"
            style={{
              paddingLeft: 'calc(env(safe-area-inset-left, 0px) + var(--tg-safe-area-inset-left, 0px))',
              paddingRight: 'calc(env(safe-area-inset-right, 0px) + var(--tg-safe-area-inset-right, 0px))',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--tg-safe-area-inset-bottom, 0px))',
            }}
          >
            {/* Safe area spacer for Telegram Mini Apps */}
            <div className="md:hidden" style={{ height: 'calc(env(safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px))' }} />
            <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-[#23262F] flex-shrink-0 sticky top-0 bg-[#181A20] z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">
                  Как получить User ID и Hash getPaySystemInfo?
                </h2>
                <button
                  onClick={() => setShowInstructions(false)}
                  className="text-[#B1B5C3] hover:text-white transition-colors p-2 -mr-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
              <div className="space-y-4 text-[#B1B5C3]">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Пошаговая инструкция</h3>
                  
                  <div className="bg-blue-900/20 border border-blue-500 text-blue-400 px-4 py-3 rounded-lg mb-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <span className="font-medium">Скачать Proxyman</span>
                    </div>
                    <p className="mb-2">Для получения User ID и Hash getPaySystemInfo необходимо установить приложение Proxyman на iPhone (для перехвата сетевых запросов):</p>
                    <a 
                      href="https://apps.apple.com/ru/app/proxyman-network-debug-tool/id1551292695" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-blue-300 hover:text-blue-200 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 1.343-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      <span>Открыть в App Store</span>
                    </a>
                  </div>
                  
                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>Установите Proxyman на iPhone</li>
                    <li>Настройте SSL сертификат по инструкции Proxyman</li>
                    <li>Запустите Proxyman и включите перехват</li>
                    <li>Запустите GuruApp (это создаст первый запрос)</li>
                    <li>Вернитесь в Proxyman и найдите папку <code className="bg-[#353945] px-2 py-1 rounded">app.rewardguru.limited</code></li>
                    <li>Выберите первый запрос и нажмите "Enable SSL Proxying"</li>
                    <li>Вернитесь в GuruApp и перейдите в раздел "Деньги" → "Карта"</li>
                    <li>Сверните приложение GuruApp и вернитесь в Proxyman</li>
                    <li>В папке <code className="bg-[#353945] px-2 py-1 rounded">app.rewardguru.limited</code> найдите новый запрос в самом вверху</li>
                    <li>Нажмите "View Body" для просмотра содержимого запроса</li>
                    <li>Скопируйте значения:
                      <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                        <li><code className="bg-[#353945] px-2 py-1 rounded">user_id</code> - короткая строка символов</li>
                        <li><code className="bg-[#353945] px-2 py-1 rounded">hash</code> - длинная строка символов</li>
                      </ul>
                    </li>
                    <li>Копируйте значения без кавычек и вставьте в соответствующие поля</li>
                  </ol>
                </div>

                <div className="bg-yellow-900/20 border border-yellow-500 text-yellow-400 px-4 py-3 rounded-lg">
                  <strong>Важно:</strong> Эти данные необходимы для корректной работы с API и для получения актуального баланса устройства и новых заказов. Данные не передаются третьим лицам и не предоставляют возможность вывода средств.
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-[#23262F] flex-shrink-0 sticky bottom-0 bg-[#181A20] z-10">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowInstructions(false)}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceModal; 